import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";

// Landscape, matches the customer menu card (h-44 wide box, object-cover).
const ASPECT = 16 / 9;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getCroppedDataUrl(src, area) {
  const img = await loadImage(src);
  const maxW = 900;
  const scale = area.width > maxW ? maxW / area.width : 1;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(area.width * scale));
  canvas.height = Math.max(1, Math.round(area.height * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", 0.85);
}

export default function ImageCropModal({ open, src, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPx, setAreaPx] = useState(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_area, px) => setAreaPx(px), []);

  const reset = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setAreaPx(null); };

  const apply = async () => {
    if (!areaPx) return;
    setBusy(true);
    try {
      const dataUrl = await getCroppedDataUrl(src, areaPx);
      onCropped(dataUrl);
      reset();
    } catch (e) {
      toast.error("Couldn't crop this image. If it's an external web link, upload the picture file instead.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => { reset(); onCancel(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) cancel(); }}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0" data-testid="image-crop-modal">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-heading font-bold text-lg">Crop image</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Drag to reposition and zoom — this frame is exactly what customers see on the menu card.</p>
        </div>
        <div className="relative w-full h-64 bg-metros-black">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={ASPECT}
              objectFit="contain"
              showGrid={true}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold w-12">Zoom</span>
            <input
              type="range" min={1} max={3} step={0.01} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              data-testid="crop-zoom-slider"
              className="flex-1 accent-metros-red"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancel} data-testid="crop-cancel-btn"
              className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="button" onClick={apply} disabled={busy || !areaPx} data-testid="crop-apply-btn"
              className="rounded-full bg-metros-red text-white px-5 py-2 text-sm font-bold disabled:opacity-60">{busy ? "Saving…" : "Use this crop"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
